import { CalendarIcon, RepeatIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { DateDisplay } from '@/components/ui/date-display';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { getFrequencyDescription, type Gift } from '@/models/gift';

import { getGiftTimelineStatus } from '../libs/timeline';

/**
 * A gift's window and claim frequency. `published` overrides the gift's own
 * state — the publish preview shows the timeline the gift is about to have.
 */
export const GiftSchedule = ({
  gift,
  published = !!gift.published_at,
}: {
  gift: Pick<
    Gift,
    'available_at' | 'expires_at' | 'claim_frequency' | 'published_at'
  >;
  published?: boolean;
}) => {
  const timeline = getGiftTimelineStatus(
    gift.available_at,
    gift.expires_at,
    published,
  );

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-start gap-2 text-sm text-foreground">
        <CalendarIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <div className="flex flex-col items-start">
          <span
            className={cn(
              'font-bold transition-all text-xs',
              timeline.className,
            )}
          >
            {timeline.label}
          </span>
          <DateDisplay
            date={{
              from: new Date(gift.available_at),
              to: gift.expires_at ? new Date(gift.expires_at) : undefined,
            }}
            formatString="PP, HH:mm"
            rangeSeparator=" - "
            showOffset
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <RepeatIcon className="h-3 w-3 shrink-0 text-muted-foreground" />
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="secondary" className="cursor-help capitalize">
              {gift.claim_frequency}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p>{getFrequencyDescription(gift.claim_frequency)}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
};
